import { ImpersonationContextKey, ModalChildComponentContextKey } from '@AppModels/.';
import * as SharedAppState from '@AppModels/AppState';
import { EventLogger } from '@AppModels/Logging';
import * as Api from '@ViewModels';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { css } from 'aphrodite';
import { action, autorun, IReactionDisposer, makeObservable, observable } from 'mobx';
import { observer, Provider } from 'mobx-react';
import * as React from 'react';
import { CredentialStore } from '../../../api/CredentialStore';
import {
	FullScreenModalViewModel,
	IUserSessionContextConfig,
	UserSessionContext,
} from '../../../viewmodels/AppViewModels';
import { BrowserPushNotificationsViewModel } from '../../../viewmodels/PushNotifications';
import { NuqsRR5Adapter } from '../../NuqsAdapter';
import { ReactQueryProvider } from '../../ReactQueryProvider';
import { SonnerToast } from '../../components/SonnerToast';
import { TooltipProvider } from '../../components/Tooltip';
import Benny from '../../fonts/Benny.ttf';
import Erin from '../../fonts/Erin.ttf';
import John from '../../fonts/John.ttf';
import Moss from '../../fonts/Moss.ttf';
import { FocusedViewProvider } from '../Contact/Focused/FocusedViewProvider';
import { HIPAATermsAcknowledgementProvider } from '../HIPAATermsAcknowledgement/HIPAATermsAcknowledgementProvider';
import { TextingCampaignRegistrationContextProvider } from '../Texting/TextingCampaignRegistrationPrompt/context';
import { AIAssistantStreamingMutationProvider } from '../templates/AssistantChat';
import { styleSheet } from './styles';

interface IProps {
	userSessionConfig?: IUserSessionContextConfig;
}

interface IState {
	isLoading?: boolean;
}

class _App extends React.Component<React.PropsWithChildren<IProps>, IState> {
	private userSession: UserSessionContext;
	private mMounted: boolean;
	private mLoggedInDisposer: IReactionDisposer;
	public readonly state: IState = {
		isLoading: true,
	};

	constructor(props: IProps) {
		super(props);

		makeObservable<_App, 'mMounted'>(this, {
			mMounted: observable,
			init: action,
		});

		const userSessionConfig: IUserSessionContextConfig = {
			...props.userSessionConfig,
			apiConfig: {
				...(
					props.userSessionConfig || {
						apiConfig: {
							baseUrl: import.meta.env.VITE_API_URL,
						},
					}
				).apiConfig,
				credentialStore: new CredentialStore(),
			},
		};

		this.userSession = new UserSessionContext(userSessionConfig);
		SharedAppState.AppState[SharedAppState.UserSessionViewModelKey] = this.userSession;

		SharedAppState.AppState[SharedAppState.ActionItemComposerViewModelKey].setUserSession(this.userSession);

		SharedAppState.AppState[SharedAppState.NoteComposerViewModelKey].setUserSession(this.userSession);

		SharedAppState.AppState[SharedAppState.QuickAddEntityViewModelKey].setUserSession(this.userSession);

		SharedAppState.AppState[SharedAppState.SingleEmailComposerKey].setUserSession(this.userSession);
		SharedAppState.AppState[SharedAppState.TextMessagingViewModelKey] = new Api.TextMessagingViewModel(
			this.userSession
		);
		SharedAppState.AppState[SharedAppState.FullScreenModalViewModelKey] = new FullScreenModalViewModel();
		SharedAppState.AppState[SharedAppState.PushNotificationsViewModelKey] = new BrowserPushNotificationsViewModel(
			this.userSession,
			{
				addLogger: Api.VmUtils.Noop,
				logEvent: (args, context) => console.log(args, context),
				removeLogger: Api.VmUtils.Noop,
			}
		);
	}

	init() {
		this.mMounted = true;

		import(/* webpackChunkName: "session-replay" */ '../../../models/SessionReplayLogger')
			?.then(mod => {
				// @ts-ignore
				const SessionReplayLogger: new (env: string, appId: string) => Api.ILogger<Api.ILogEvent> = mod.default;

				const sessionReplayLogger = new SessionReplayLogger(
					import.meta.env.MODE,
					import.meta.env.VITE_SESSION_REPLAY_APPID
				);
				Api.EventLogger.addLogger(sessionReplayLogger);
			})
			.catch(() => {
				console.error('Failed to load Session Replay');
			});
		new FontFace('Aladdin', `url(${Moss})`, {
			style: 'normal',
			weight: 'normal',
		}).load();
		new FontFace('Cinderella', `url(${Erin})`, {
			style: 'normal',
			weight: 'normal',
		}).load();
		new FontFace('Donald', `url(${Benny})`, {
			style: 'normal',
			weight: 'normal',
		}).load();
		new FontFace('Nemo', `url(${Erin})`, {
			style: 'normal',
			weight: 'normal',
		}).load();
		new FontFace('Scar', `url(${Erin})`, {
			style: 'normal',
			weight: 'normal',
		}).load();
		new FontFace('Tarzan', `url(${John})`, {
			style: 'normal',
			weight: 'normal',
		}).load();
		new FontFace('Woody', `url(${Benny})`, {
			style: 'normal',
			weight: 'normal',
		}).load();

		this.mLoggedInDisposer = autorun(() => {
			if (!!this.mMounted && !!this.userSession?.isAuthenticated) {
				SharedAppState.AppState[SharedAppState.TextMessagingViewModelKey].init(EventLogger);
			}
		});
		const promise = this.userSession.load();
		const onFinish = () => {
			if (this.mMounted) {
				this.setState({
					isLoading: false,
				});
			}
		};
		if (promise) {
			promise.then(onFinish).catch(onFinish);
		} else {
			onFinish();
		}
	}

	public componentDidMount() {
		this.init();
	}

	public componentWillUnmount() {
		this.mMounted = false;
		this.mLoggedInDisposer?.();

		this.mLoggedInDisposer = null;
	}

	public render() {
		return (
			<TooltipProvider>
				<ReactQueryProvider>
					<Provider
						{...SharedAppState.AppState}
						{...{ [ModalChildComponentContextKey]: null }}
						{...{ [ImpersonationContextKey]: null }}
					>
						<NuqsRR5Adapter>
							<HIPAATermsAcknowledgementProvider>
								<TextingCampaignRegistrationContextProvider>
									<FocusedViewProvider>
										<AIAssistantStreamingMutationProvider>
											<div className={`${css(styleSheet.container)} root`}>
												{this.state.isLoading ? <span style={{ display: 'none' }} /> : this.props.children}
											</div>
										</AIAssistantStreamingMutationProvider>
									</FocusedViewProvider>
								</TextingCampaignRegistrationContextProvider>
							</HIPAATermsAcknowledgementProvider>
						</NuqsRR5Adapter>
					</Provider>
					<ReactQueryDevtools initialIsOpen={false} buttonPosition='bottom-left' />
				</ReactQueryProvider>
				<SonnerToast />
			</TooltipProvider>
		);
	}
}

export const App = observer(_App);
